import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Open } from '@sas/react-svg';
import { checkAndUploadFiles, upload } from '.';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { CompositeInput } from '../CompositeInput';
import { InvisibleText } from '../InvisibleText';
import bundle from './NovaComponents-FileUploader-gui-icu.properties';

// Import CSS last
import cn from './FileUploader.css.json';
import './FileUploader.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('FileUploader');
export const FileUploader = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const inputRef = useRef(null);
  const inputTextRef = useRef(null);
  const compositeButtonRef = useRef(null);
  const formRef = useRef(null);
  const buttonRef = useRef(null);
  const invisibleTextId = useHtmlId();
  const buttonText = props.buttonText ?? localize('buttonText.label');
  const buttonTooltip = props.iconOnly ? props.buttonTooltip || buttonText : undefined;
  const buttonAriaLabel = props.iconOnly ? props.buttonAriaLabel : undefined;
  const defaultName = 'fileUploader';
  const [value, setValue] = useState(props.value ?? '');
  const iconDescriptor = props.iconOnly ? props.icon ?? Open : props.icon;
  const [lastSelectedFiles, setLastSelectedFiles] = useState([]);
  const fieldRequired = props.required;
  const [isFileUploaderOpen, setIsFileUploaderOpen] = useState(false);

  // accept list
  // if a restricted list (restrictedFileTypes)
  // then remove those
  // as acceptable file types from
  // the file type list (fileType)
  const getFileTypes = () => {
    const restrictedFileTypes = props.restrictedFileType;
    const fileTypes = props.fileType;
    const finalFileTypes = [];
    if (restrictedFileTypes && restrictedFileTypes.length > 0 && fileTypes && fileTypes.length > 0) {
      for (const fileType of fileTypes) {
        if (restrictedFileTypes.indexOf(fileType) === -1) {
          finalFileTypes.push(fileType);
        }
      }
      return finalFileTypes && '.'.concat(finalFileTypes.join(',.'));
    }
    return fileTypes && '.'.concat(fileTypes.join(',.'));
  };
  const openFileUploader = useCallback(() => {
    setIsFileUploaderOpen(true);
    inputRef?.current?.click();
  }, [inputRef]);
  const resetForm = () => {
    formRef?.current?.reset();
  };
  const showInputField = !props.buttonOnly && /*#__PURE__*/_jsx(CompositeInput, {
    testId: postfixTestId(props.testId, 'compinput'),
    isSelectorOpen: isFileUploaderOpen,
    value: value,
    className: cn.compositeInput,
    disabled: props.disabled,
    mode: "select",
    buttonDisplay: iconDescriptor ? iconDescriptor : buttonText,
    ariaRoleDescription: props.multiple ? localize('fileUploaderMultipleRoleDescription.aria') : localize('fileUploaderRoleDescription.aria'),
    ariaDescribedBy: props.ariaDescribedBy,
    entryFieldRef: inputTextRef,
    buttonRef: compositeButtonRef,
    ariaLabel: props.ariaLabel,
    ariaLabelledBy: props.ariaLabelledBy,
    buttonProps: {
      tooltip: buttonTooltip,
      ariaLabel: buttonAriaLabel
    },
    entryFieldProps: {
      id: props.id,
      required: fieldRequired,
      showClearButton: !props.removeClearButton,
      type: 'text',
      onChange: e => {
        if (e) {
          setValue(e.value);
          if (!e.value || e.value === '') {
            setLastSelectedFiles([]);
            props.onChange?.({
              newValue: '',
              files: undefined,
              fileName: ''
            });
          }
        }
      },
      onMouseDown: e => {
        if (e.target === inputTextRef.current) {
          // not close button
          openFileUploader();
        }
      }
    }
  });
  const changeHandler = () => {
    processAndUpload();
  };
  const processAndUpload = triggerUpload => {
    const files = inputRef?.current?.files;
    if (files && files?.length > 0) {
      const {
        value,
        filesSelected
      } = checkAndUploadFiles(props, files, triggerUpload);
      if (value) {
        setValue(value);
        setLastSelectedFiles(filesSelected);
      }
    } else if (triggerUpload && lastSelectedFiles && lastSelectedFiles.length > 0) {
      upload(props, lastSelectedFiles.map(file => file.value));
    }
    resetForm();
  };

  // trigger upload on FileUploader
  useImperativeHandle(ref, () => ({
    uploadFiles: () => {
      processAndUpload(true);
    },
    focus: () => {
      if (props.buttonOnly && buttonRef.current) {
        buttonRef.current.focus();
      } else if (inputTextRef.current) {
        inputTextRef.current.focus();
      }
    },
    clearInput: () => {
      setValue('');
      props.onChange?.({
        newValue: '',
        files: undefined,
        fileName: ''
      }); // trigger onchange, set vaue to blank
    }
  }));

  useEffect(() => {
    function handleInputKeyDown(e) {
      const key = e.key;

      // Enter key using default behavior associated with
      // CompositeInput component, code there to make Enter work
      // for select mode
      switch (key) {
        case ' ':
          openFileUploader();
          e.stopPropagation();
          e.preventDefault();
          break;
        case 'Spacebar':
          openFileUploader();
          e.stopPropagation();
          e.preventDefault();
          break;
        case 'Tab':
          setIsFileUploaderOpen(false);
          break;
        case 'Enter':
          openFileUploader();
          e.preventDefault();
          e.stopPropagation();
          break;
        case 'Escape':
          setIsFileUploaderOpen(false);
          break;
        default:
          // do nothing
          break;
      }
    }
    const input = inputTextRef.current;
    const button = compositeButtonRef.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    button?.addEventListener('keydown', handleInputKeyDown);
    button?.addEventListener('click', openFileUploader);
    return function () {
      input?.removeEventListener('keydown', handleInputKeyDown);
      button?.removeEventListener('keydown', handleInputKeyDown);
      button?.removeEventListener('click', openFileUploader);
    };
  }, [value, openFileUploader]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": props.testId,
    tabIndex: -1,
    style: props.style,
    children: /*#__PURE__*/_jsxs("form", {
      encType: "multipart/form-data",
      ref: formRef,
      children: [!props.disabled ? /*#__PURE__*/_jsx(InvisibleText, {
        id: invisibleTextId,
        children: localize('fileloaderDescription.aria')
      }) : null, /*#__PURE__*/_jsxs("div", {
        onMouseLeave: () => setIsFileUploaderOpen(false),
        children: [/*#__PURE__*/_jsxs("div", {
          className: cn.inputInfo,
          children: [/*#__PURE__*/_jsx("div", {
            className: cn.inputMask,
            children: /*#__PURE__*/_jsx("input", {
              type: "file",
              "aria-hidden": "true",
              hidden: true,
              onChange: changeHandler,
              capture: false,
              tabIndex: -1,
              name: props.name ?? defaultName,
              accept: getFileTypes(),
              disabled: props.disabled,
              readOnly: true,
              ref: inputRef,
              multiple: props.multiple
            })
          }), showInputField]
        }), props.buttonOnly ? /*#__PURE__*/_jsx(Button, {
          testId: props.iconOnly ? postfixTestId(props.testId, 'icon') : postfixTestId(props.testId, 'button'),
          disabled: props.disabled,
          variant: iconDescriptor && props.buttonOnly ? ButtonVariant.Light : ButtonVariant.Standard,
          text: !props.iconOnly ? buttonText : undefined,
          tooltip: buttonTooltip,
          ariaLabel: buttonAriaLabel,
          icon: iconDescriptor ? {
            icon: iconDescriptor
          } : undefined,
          focusable: props.focusable ?? true,
          ref: buttonRef,
          onPress: () => {
            setIsFileUploaderOpen(false);
            openFileUploader();
          }
        }) : null]
      })]
    })
  });
});
//# sourceMappingURL=FileUploader.js.map